import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useResumeTailoring } from '@/contexts/ResumeTailoringContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, FileText, ArrowRight, Loader2, Library, ClipboardPaste, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { ResumeLibraryService } from '@/services/resumeLibraryService';
import { convertPDFToText } from '@/services/pdfConversionService';
import { supabase } from '@/integrations/supabase/client';
import { StepHeader } from './StepHeader';

export function ResumeSelectionPage() {
  const navigate = useNavigate();
  const { state, dispatch } = useResumeTailoring();
  const { user } = useAuth();
  const { toast } = useToast();

  const [uploadMethod, setUploadMethod] = useState<'saved' | 'upload' | 'paste'>(
    state.selectedResumeId ? 'saved' : state.uploadedFile ? 'upload' : 'paste'
  );
  const [userResumes, setUserResumes] = useState<any[]>([]);
  const [loadingResumes, setLoadingResumes] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (user && uploadMethod === 'saved') loadUserResumes();
  }, [user, uploadMethod]);

  const loadUserResumes = async () => {
    setLoadingResumes(true);
    try {
      const resumes = await ResumeLibraryService.getSavedResumes();
      setUserResumes(resumes);
    } catch {
      toast({ title: 'Error loading resumes', variant: 'destructive' });
    } finally {
      setLoadingResumes(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    setProcessing(true);
    try {
      let resumeText = '';
      if (file.type === 'application/pdf') {
        const fileName = `resume-${Date.now()}.pdf`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('documents').upload(fileName, file);
        if (uploadError) throw new Error(uploadError.message);
        const { data: { publicUrl } } = supabase.storage.from('documents').getPublicUrl(uploadData.path);
        const result = await convertPDFToText(publicUrl);
        resumeText = result.text || '';
      } else if (file.type === 'text/plain') {
        resumeText = await file.text();
      } else {
        throw new Error('Please upload a PDF or TXT file.');
      }
      dispatch({ type: 'SET_RESUME_DATA', payload: { uploadedFile: file, processedResumeData: resumeText } });
      toast({ title: 'Resume uploaded!', description: 'Your resume is ready for tailoring.' });
    } catch (error) {
      toast({ title: 'Error', description: error instanceof Error ? error.message : 'Please try again.', variant: 'destructive' });
    } finally {
      setProcessing(false);
    }
  };

  const handleSavedResumeSelect = (resumeId: string) => {
    const resume = userResumes.find(r => r.id === resumeId);
    if (resume) {
      dispatch({ type: 'SET_RESUME_DATA', payload: { selectedResumeId: resumeId, processedResumeData: resume.resume_text || resume.content } });
    }
  };

  const handlePastedTextChange = (text: string) => {
    dispatch({ type: 'SET_RESUME_DATA', payload: { pastedResumeText: text, processedResumeData: text } });
  };

  const canContinue = Boolean(state.processedResumeData || state.selectedResumeId || state.pastedResumeText);

  const handleContinue = () => {
    if (!canContinue) {
      toast({ title: 'Please provide your resume', variant: 'destructive' });
      return;
    }
    navigate('/resume-tailoring-simple/step/2');
  };

  const methods = [
    { id: 'saved', label: 'Resume Library', description: 'Choose from your saved resumes', icon: Library },
    { id: 'upload', label: 'Upload File', description: 'Upload a PDF or TXT file', icon: Upload },
    { id: 'paste', label: 'Paste Text', description: 'Copy & paste resume text directly', icon: ClipboardPaste },
  ] as const;

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-8">
      <StepHeader currentStep={1} />

      {/* Method selector */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Choose Your Resume
          </CardTitle>
          <CardDescription>How would you like to provide your resume?</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {methods.map(({ id, label, description, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setUploadMethod(id)}
                className={`text-left p-4 rounded-xl border-2 transition-all ${
                  uploadMethod === id
                    ? 'border-primary bg-primary/5 shadow-sm'
                    : 'border-border hover:border-primary/40'
                }`}
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${uploadMethod === id ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <p className="font-semibold text-sm">{label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Saved resumes */}
      {uploadMethod === 'saved' && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Select from Library</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingResumes ? (
              <div className="flex items-center gap-2 py-6 justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading resumes…
              </div>
            ) : userResumes.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Library className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No saved resumes yet</p>
                <p className="text-sm mt-1">Upload a file or paste text below</p>
              </div>
            ) : (
              <Select value={state.selectedResumeId} onValueChange={handleSavedResumeSelect}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select a resume…" />
                </SelectTrigger>
                <SelectContent>
                  {userResumes.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.resume_name || r.title || `Resume ${r.id.slice(0, 8)}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload */}
      {uploadMethod === 'upload' && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Upload Resume File</CardTitle>
          </CardHeader>
          <CardContent>
            <label
              htmlFor="resume-upload"
              className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                state.uploadedFile ? 'border-green-400 bg-green-50' : 'border-border hover:border-primary/50 hover:bg-primary/5'
              }`}
            >
              {state.uploadedFile ? (
                <>
                  <CheckCircle2 className="h-10 w-10 mx-auto text-green-500 mb-2" />
                  <p className="font-semibold text-green-700">{state.uploadedFile.name}</p>
                  <p className="text-sm text-green-600 mt-1">Click to replace</p>
                </>
              ) : processing ? (
                <>
                  <Loader2 className="h-10 w-10 mx-auto text-primary mb-2 animate-spin" />
                  <p className="font-medium">Processing resume…</p>
                </>
              ) : (
                <>
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                  <p className="font-semibold">Click to upload</p>
                  <p className="text-sm text-muted-foreground mt-1">PDF or TXT · Max 10MB</p>
                </>
              )}
            </label>
            <Input
              id="resume-upload"
              type="file"
              accept=".pdf,.txt"
              className="hidden"
              onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }}
            />
          </CardContent>
        </Card>
      )}

      {/* Paste */}
      {uploadMethod === 'paste' && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-base">Paste Resume Text</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              placeholder="Paste your full resume text here…"
              value={state.pastedResumeText}
              onChange={e => handlePastedTextChange(e.target.value)}
              rows={14}
              className="font-mono text-sm resize-none"
            />
            {state.pastedResumeText && (
              <p className="text-xs text-muted-foreground mt-2">{state.pastedResumeText.length.toLocaleString()} characters</p>
            )}
          </CardContent>
        </Card>
      )}

      {canContinue && (
        <div className="flex items-center gap-2 p-4 rounded-xl bg-green-50 border border-green-200 mb-6">
          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
          <p className="text-sm text-green-800 font-medium">Resume ready — click Continue to proceed</p>
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" disabled>Back</Button>
        <Button onClick={handleContinue} disabled={!canContinue || processing} size="lg">
          Continue <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
